import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, Trash2 } from "lucide-react";
import { api, errMsg, downloadFile, saveBlob } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MEDIA = [["music", "muzyka"], ["music_photo", "muzyka + zdjęcie"], ["video", "animacja"]];

export const MediaTab = () => {
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState([]);
  useEffect(() => { api.get("/admin/plan").then((r) => setDays(r.data.days)); }, []);
  const get = async (m, label, day) => {
    const key = `${day}|${m}`;
    setBusy(key);
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get("/admin/signups/media-count", { params: { media: m, day } });
      if (!data.count) { win?.close(); toast.error(`${day}: brak zatwierdzonych zgłoszeń z multimediami „${label}”`); return; }
      await downloadFile({ path: "/admin/signups/media.zip", params: { media: m, day }, filename: `multimedia-${day}-${label.replace(/\s\+\s/g, "-")}.zip`, win });
    } catch (e) { win?.close(); toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  return (
    <section className="fly-card p-5 mt-6" data-testid="media-tab">
      <h2 className="font-display text-xl font-bold uppercase">Multimedia</h2>
      <p className="text-sm text-slate-400 mt-1">Pobierz pliki zatwierdzonych zgłoszeń przypisanych do danego dnia w Planie zawodów jako ZIP: jeden folder na dzień, pliki ułożone po kolei i nazwane numerem występu z planu (np. 007) oraz imieniem zawodnika lub nazwą grupy.</p>
      <div className="grid gap-3 mt-4">
        {days.map((d) => (
          <div key={d} className="rounded-xl bg-[#0B132B]/60 border border-white/5 p-3 flex flex-wrap items-center gap-3" data-testid={`media-day-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="font-display text-lg font-bold uppercase w-full sm:w-56">{d}</span>
            {MEDIA.map(([m, label]) => (
              <button key={m} data-testid={`media-export-${m}-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} disabled={!!busy} onClick={() => get(m, label, d)} className="h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {busy === `${d}|${m}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}{label}
              </button>
            ))}
          </div>
        ))}
        {!days.length && <p className="text-sm text-slate-500">Dodaj dni w Planie zawodów.</p>}
      </div>
    </section>
  );
};

export const DeleteAllSignups = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/signups/delete-all", { password: pw, check_only: true });
      await downloadFile({ path: "/admin/signups.csv", filename: "zapisy-fly-cup-kopia.csv" });
      const { data } = await api.post("/admin/signups/delete-all", { password: pw });
      toast.success(`Usunięto zgłoszenia: ${data.deleted}. Pliki usunięte z dysku: ${data.files_removed} z ${data.files}`);
      setOpen(false); setPw("");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="signups-delete-all-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-semibold flex items-center gap-2"><Trash2 className="w-4 h-4" />Usuń wszystkie zgłoszenia</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C2541] border-red-500/40" data-testid="signups-delete-all-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase text-red-300">Usuń wszystkie zgłoszenia</DialogTitle></DialogHeader>
          <form onSubmit={run} className="grid gap-3">
            <p className="text-sm text-slate-300">Najpierw pobierze się kopia wszystkich zgłoszeń (CSV). Potem zostaną usunięte wszystkie zgłoszenia oraz ich pliki (muzyka, zdjęcia, animacje, zgody). Lista startowa w Organizatorze zostaje bez zmian.</p>
            <input data-testid="signups-delete-all-password" type="password" required placeholder="Hasło administratora" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 px-3 rounded-xl bg-[#0B132B] border border-red-500/30 text-sm" />
            <button data-testid="signups-delete-all-confirm" disabled={busy || !pw} className="h-11 rounded-xl bg-red-600 hover:bg-red-500 font-semibold disabled:opacity-50">{busy ? "Usuwam…" : "Pobierz CSV i usuń"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ImportSignupsCsv = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", pw);
      const { data } = await api.post("/admin/startlist/import-signups", fd);
      try {
        const r = await api.get(`/admin/backups/${data.backup_id}/file.xlsx`, { responseType: "blob" });
        saveBlob(r.data, data.filename);
      } catch { /* backup stays on server */ }
      const n = data.imported, w = n === 1 ? "występ" : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? "występy" : "występów";
      toast.success(`Wgrano ${n} ${w}${data.skipped ? `, pominięto ${data.skipped} (niezatwierdzone lub bez dnia)` : ""}`);
      setOpen(false); setPw(""); setFile(null);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="import-signups-csv-btn" onClick={() => setOpen(true)} className="h-11 px-4 rounded-xl border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 font-semibold text-sm flex items-center gap-2"><FileDown className="w-4 h-4 rotate-180" />Wgraj CSV zapisów</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C2541] border-amber-500/40" data-testid="import-signups-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Wgraj CSV zapisów</DialogTitle></DialogHeader>
          <form onSubmit={run} className="grid gap-3">
            <p className="text-sm text-slate-300">Plik CSV pobrany z panelu Zapisy. Wgrane zostaną tylko zatwierdzone zgłoszenia z przypisanym dniem (z godziną i kolejnością). <b className="text-red-300">Zastępuje całą listę startową</b> – oceny i rejestracja zostaną usunięte, przed tym zapisze się kopia wyników.</p>
            <input data-testid="import-signups-file" type="file" accept=".csv,text/csv" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
            <input data-testid="import-signups-password" type="password" required placeholder="Hasło administratora" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 px-3 rounded-xl bg-[#0B132B] border border-amber-500/30 text-sm" />
            <button data-testid="import-signups-confirm" disabled={busy || !pw || !file} className="h-11 rounded-xl bg-amber-600 hover:bg-amber-500 font-semibold disabled:opacity-50">{busy ? "Wgrywam…" : "Wgraj i zastąp listę"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
